import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  lineDiscount,
  isDiscountableType,
  totalDiscountableQty,
  cartQualifiesForBulk,
  BULK_DISCOUNT_MIN_QTY,
} from "@/lib/beddingConfig";

const CartContext = createContext(null);

const STORAGE_KEY = "bl_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Track the last-known "qualifies" state so we toast only once per transition
  const wasQualified = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  // Toast once when the cart transitions into qualified state
  useEffect(() => {
    const qualifies = cartQualifiesForBulk(items);
    if (qualifies && !wasQualified.current) {
      toast.success("Your cart is entitled for a 5% discount", {
        description: `${BULK_DISCOUNT_MIN_QTY}+ sheets in cart — applied at checkout`,
        duration: 4500,
      });
    }
    wasQualified.current = qualifies;
  }, [items]);

  const addItem = (item) => {
    setItems((prev) => {
      // Same product + same config → merge qty
      const idx = prev.findIndex(
        (i) => i.product_id === item.product_id && JSON.stringify(i.config || {}) === JSON.stringify(item.config || {})
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
        return next;
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
    setDrawerOpen(true);
  };

  const updateQty = (lineKey, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (lineKey === keyOf(i) ? { ...i, quantity: Math.max(1, qty) } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const removeItem = (lineKey) => {
    setItems((prev) => prev.filter((i) => keyOf(i) !== lineKey));
  };

  const clear = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  // Aggregate discount — 5% off every Bedsheet/Fitted-Sheet line when cart total sheets >= 3
  const discount = useMemo(
    () => items.reduce((s, i) => s + lineDiscount(i, items), 0),
    [items],
  );
  const discountQualifiesAny = useMemo(() => cartQualifiesForBulk(items), [items]);
  const discountableQty = useMemo(() => totalDiscountableQty(items), [items]);
  const discountShortfall = useMemo(
    () => Math.max(0, BULK_DISCOUNT_MIN_QTY - discountableQty),
    [discountableQty],
  );
  // Show a nudge only when the customer already has SOME sheets but is short of the threshold
  const hasAnySheets = useMemo(() => items.some(isDiscountableType), [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQty,
        removeItem,
        clear,
        subtotal,
        count,
        discount,
        discountQualifiesAny,
        discountableQty,
        discountShortfall,
        hasAnySheets,
        drawerOpen,
        setDrawerOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const keyOf = (i) => `${i.product_id}__${JSON.stringify(i.config || {})}`;

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
